import { useDispatch, useSelector } from "react-redux"
import { useEffect, useMemo, useState } from "react"
import { Button, Divider, Dropdown, message, MenuProps } from "antd"
import { WithTooltip } from "../../../../shared/components/tooltip.tsx/withTootip"
import { candidatesPerPage, errorMessage, roleError } from "../../../../shared/utils/constantData"
import { permissionsActions } from "../../../../shared/utils/interfaces/AuthInterface"
import { usePermissions } from "../../../../shared/utils/customHooks/usePermissions"
import { selectRolePerJob } from "../../../authentication/slice/authSlice"
import { GlobalStageTypesId, IAIFilter, ICandidatesFilter, IHiringStage, IRejectionReason, candidatesSorting } from "../../../../shared/utils/interfaces/candidatesListFace"
import { selectAiFilters, selectAiFiltersArray, selectCandidateProfile, selectCandidates, selectCandidatesFilters, selectCurrentHiringStages, selectIsChangingCandidateStage, setAiSearchFilter, setCandidateProfile, setCandidatesFilter, setCurrentHiringStages, setIsChangingCandidateStage, setStageChange, removeCandidateById } from "../../slice/candidateSlice"
import { getImageUrl } from "../../../../shared/utils/functions/images"
import redDisLike from '../../../../shared/assets/images/redDisLikeIcon.svg'
import grayDisLike from '../../../../shared/assets/images/twoRate.svg'
import redArrowDown from '../../../../shared/assets/images/ChevronDownRed.svg'
import revertGray from '../../../../shared/assets/images/RevertGray.svg'
import revertLightGray from '../../../../shared/assets/images/RevertLightGray.svg'
import CaretDownGray from '../../../../shared/assets/images/ChevronDownGray.svg';
import { useChangeCandidateStageMutation, useGetCandidateMovementReasonsQuery } from "../../slice/candiddateApiSlice"
import { analytics } from '../../../../shared/utils/analytics';
import { logEvent } from 'firebase/analytics';
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useCandidateNavigation } from "../../../../shared/utils/customHooks/useCandidateNavigation"
import { relocateNotification } from "../../../../shared/utils/functions/relocateNotification"
import { removeCandidateFromIds } from "../../../../shared/utils/functions/removeCandidateFromIds";
import { setMobileTabsMenuExpanded } from "../../../../shared/components/Menu/slice/tabsMenuSlice";

function RejectionAction({ isRejectionStage = false, candidateId, previousStageId, isProfilePage = false }: { isRejectionStage: boolean, candidateId: number, previousStageId?: number, isProfilePage?: boolean }) {
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const { jobId } = useParams()
    const { updateJobHiringStage } = permissionsActions
    const { hasJobPermission } = usePermissions()
    const rolePerJob = useSelector(selectRolePerJob)
    const currentStages: IHiringStage[] = useSelector(selectCurrentHiringStages);
    const [changeCandidateStage, { isLoading, isSuccess, isError, data }] = useChangeCandidateStageMutation();
    const aiFiltersArray: { id: number, question: string }[] = useSelector(selectAiFiltersArray);
    const candidatesFilter: ICandidatesFilter = useSelector(selectCandidatesFilters);
    const candidatesAiFilter: IAIFilter = useSelector(selectAiFilters);
    const [selectedStage, setSelectedStage] = useState<number | false>()
    const [isRelocated, setIsRelocated] = useState<boolean>(false);
    const candidate = useSelector(selectCandidateProfile)
    const candidates = useSelector(selectCandidates)
    const isGlobalChangingStage = useSelector(selectIsChangingCandidateStage)
    const { handleCandidateNavigate } = useCandidateNavigation()
    const { data: rejectionReasons, isError: isErrorRejectionReasons, isLoading: rejectionReasonsLoading } = useGetCandidateMovementReasonsQuery()
    const currentStagesList = useMemo(() => {
        return currentStages?.length !== 0 &&
            Array.isArray(currentStages) ? currentStages.filter((stage) => stage.globalstagetypeid !== GlobalStageTypesId.rejected).map((item: IHiringStage) => ({
                label: item?.name.charAt(0).toUpperCase() + item.name.slice(1),
                key: item?.id,
            })) : undefined;
    }, [currentStages])
    const rejectStageId = currentStages[currentStages?.length - 1]?.id;

    const relocateCandidate = () => {
        dispatch(setCandidatesFilter({ ...candidatesFilter, hiringStageId: selectedStage, sortBy: candidatesSorting[0].value }));
        if (isProfilePage) navigate(`/jobs/${candidate?.jobId}/candidates?hiringStageId=${selectedStage}`);
        else {
            navigate(`/jobs/${jobId}/candidates?hiringStageId=${selectedStage}`);
            dispatch(setMobileTabsMenuExpanded(null));
        }
    }

    useEffect(() => {
        dispatch(setIsChangingCandidateStage(isLoading));
    }, [isLoading, dispatch]);

    useEffect(() => {
        if (isSuccess) {
            if (isProfilePage) {
                dispatch(setCandidateProfile({ ...candidate, ...data?.data }))
                const selectedStageGlobalTypeId = currentStages?.find(({ id }) => id === selectedStage)?.globalstagetypeid;
                dispatch(setStageChange(selectedStageGlobalTypeId))
                if (selectedStageGlobalTypeId === GlobalStageTypesId.rejected && !location?.state?.fromSearch && !location?.state?.fromSimilarity && isProfilePage) {
                    localStorage.removeItem("currentTab");
                    handleCandidateNavigate();
                }
            } else {
                dispatch(removeCandidateById(candidateId))
                dispatch(setCurrentHiringStages(data?.data?.pipelineStages))
                removeCandidateFromIds(candidateId)
            }
            logEvent(analytics, 'Change candidate stage from candidate profile')
            if (!aiFiltersArray?.length) dispatch(setCandidatesFilter({
                ...candidatesFilter, page: 1, perPage: candidatesPerPage,
                hiringStageId: isRelocated ? selectedStage : candidatesFilter?.hiringStageId,
            }))
            else dispatch(setAiSearchFilter({ ...candidatesAiFilter, page: 1 }))
            const destinationStage = currentStages?.find(stage => stage?.id === selectedStage)?.name;
            const selectedCandidate = isProfilePage ? candidate : candidates?.find(item => item?.id === candidateId)            
            relocateNotification({ candidate: selectedCandidate, destination: destinationStage, isStage: true, onClick: () => {
                setIsRelocated(true);
                relocateCandidate();
            }});
        } else if (isError) {
            message.error(`${errorMessage}, the hiring stage is not updated`)
        }
        // eslint-disable-next-line
    }, [isError, isSuccess, dispatch, aiFiltersArray.length, data, isRelocated]);

    const handleChangeStage: MenuProps['onClick'] = async (e) => {
        if (isGlobalChangingStage) return;
        const candidateData = {
            applicationId: Number(candidateId),
            pipelineHiringStageId: Number(e?.key)
        }
        setSelectedStage(Number(e?.key))
        await changeCandidateStage(candidateData)
    };

    const handleRejection = async (e: any) => {
        if (isGlobalChangingStage) return;
        const candidateData = {
            applicationId: Number(candidateId),
            pipelineHiringStageId: rejectStageId,
            reasonId: Number(e?.key)
        }
        setSelectedStage(rejectStageId)
        await changeCandidateStage(candidateData)
    }

    const handleRevert = async (e: React.MouseEvent) => {
        if (isGlobalChangingStage) return;
        const candidateData = {
            applicationId: Number(candidateId),
            pipelineHiringStageId: previousStageId,
        }
        setSelectedStage(previousStageId)
        await changeCandidateStage(candidateData)
    }

    return (
        <WithTooltip
            visible={!hasJobPermission(rolePerJob, updateJobHiringStage)}
            placement='bottom'
            title={roleError}
        >
            <div className="flex items-center w-auto my-auto flex-nowrap group min-w-max h-fit">
                {!isRejectionStage ?
                    <>
                        <WithTooltip
                            className='w-fit'
                            visible={hasJobPermission(rolePerJob, updateJobHiringStage)}
                            placement='bottom'
                            title={'Send to Rejected'}
                        >
                            <Button
                                loading={rejectionReasonsLoading || isLoading}
                                disabled={!hasJobPermission(rolePerJob, updateJobHiringStage)}
                                className='btn-danger-light p-2 !rounded-l-md !rounded-br-none !rounded-tr-none disabled:bg-transparent flex !text-red-800'
                                title={!hasJobPermission(rolePerJob, updateJobHiringStage) ? 'Send to Rejected' : ''}
                                onClick={(e) => handleRejection(e)}
                            >
                                <img className="w-4 h-4" src={hasJobPermission(rolePerJob, updateJobHiringStage) ? redDisLike : grayDisLike} alt='Rejected' />
                            </Button>
                        </WithTooltip>
                        <Dropdown
                            className={`mr-0 cursor-pointer rounded-r-md p-2 ${!hasJobPermission(rolePerJob, updateJobHiringStage) ? '!cursor-not-allowed !bg-transparent border-r border-y !border-y-gray-300 !border-r-gray-300' : 'border-l !border-l-red-200'} btn-danger-light shadow-sm`}
                            trigger={['hover']}
                            disabled={!hasJobPermission(rolePerJob, updateJobHiringStage) || rejectionReasonsLoading || isLoading}
                            menu={{
                                items: [
                                    {
                                        type: "group",
                                        label: (
                                            <>
                                                <p className="px-4 py-2 text-sm font-bold text-gray-600">Select candidate rejection reason</p>
                                                <Divider className='my-0 border-gray-300' />
                                            </>
                                        ),
                                        children: isErrorRejectionReasons ? [
                                            {
                                                key: 'error',
                                                label: <div className="p-1 text-base rounded hover:!bg-transparent cursor-text">
                                                    No rejection reasons available
                                                </div>,
                                                className: "hover:!bg-transparent cursor-text",
                                            }
                                        ]
                                            : rejectionReasons?.data?.map((item: IRejectionReason) => ({
                                                key: String(item?.id),
                                                label: <div className="p-1 w-full text-base rounded hover:!bg-indigo-50">{item?.reason}</div>,
                                                className: "hover:!bg-indigo-50",
                                                onClick: (e: React.MouseEvent) => { handleRejection(e) },
                                            })),
                                    },
                                ],
                                className: "overflow-y-auto bg-white border border-gray-300 rounded-md shadow-md max-h-52",
                            }}
                        >
                            <img src={!hasJobPermission(rolePerJob, updateJobHiringStage) ?
                                getImageUrl('grayArrowDown') : redArrowDown} alt='arrow' />
                        </Dropdown>
                    </>
                    : <>
                        <WithTooltip
                            className='w-fit'
                            visible={hasJobPermission(rolePerJob, updateJobHiringStage)}
                            placement='bottom'
                            title={'Revert candidate'}
                        >
                            <Button
                                loading={isLoading}
                                disabled={!hasJobPermission(rolePerJob, updateJobHiringStage)}
                                className={`flex items-center bg-white gap-1 px-2 p-2 !rounded-l-md !border-gray-300 !rounded-br-none !rounded-tr-none ${isLoading ? 'opacity-60 bg-gray-400' : ''}`}
                                title={!hasJobPermission(rolePerJob, updateJobHiringStage) ? 'Revert candidate' : ''}
                                onClick={(e) => { handleRevert(e) }}
                            >
                                <img className="w-4 h-4" src={hasJobPermission(rolePerJob, updateJobHiringStage) ? revertGray : revertLightGray} alt='Revert' />
                            </Button>
                        </WithTooltip>
                        <Dropdown
                            className={`mr-0 cursor-pointer rounded-r-md p-[0.331rem] ${!hasJobPermission(rolePerJob, updateJobHiringStage) ? "!bg-transparent !cursor-not-allowed !p-2" : isLoading ? "cursor-wait opacity-60" : ""} btn-secondary border border-gray-300 border-l-0 shadow-sm`}
                            trigger={["hover"]}
                            disabled={!hasJobPermission(rolePerJob, updateJobHiringStage) || isLoading}
                            menu={{
                                items: [
                                    {
                                        type: "group",
                                        label: (
                                            <>
                                                <p className="px-4 py-2 text-sm font-bold text-gray-600">
                                                    Revert candidate
                                                    <br />
                                                    and send to
                                                </p>
                                                <Divider className='my-0 border-gray-300' />
                                            </>
                                        ),
                                        children: currentStagesList?.map((item) => ({
                                            key: String(item?.key),
                                            label: <div className="p-1 w-full text-base rounded hover:!bg-indigo-50">{item?.label}</div>,
                                            className: `hover:!bg-indigo-50 ${isLoading ? "hover:!cursor-wait" : ""}`,
                                        })),
                                    },
                                ],
                                onClick: !isLoading ? handleChangeStage : () => { },
                                className: "overflow-y-auto bg-white border border-gray-300 rounded-md shadow-md max-h-64",
                            }}
                        >
                            <img
                                src={
                                    !hasJobPermission(rolePerJob, updateJobHiringStage)
                                        ? getImageUrl("grayArrowDown")
                                        : CaretDownGray
                                }
                                alt="arrow"
                            />
                        </Dropdown>
                    </>
                }
            </div>
        </WithTooltip>
    )
}
export default RejectionAction